import "../styles/AdminInterface.css"
import React, { useState, useEffect, useMemo } from 'react'
import BackendAPI from "../API/BackendAPI"
import IconButton from '@mui/material/IconButton';

import { useHistory } from 'react-router-dom';
import { FormControl, InputLabel, Tooltip, Divider, Typography, TableContainer, TableHead, TableRow, Paper, TableBody, Table, TableCell, Select, MenuItem, Button } from "@material-ui/core"
import DownloadIcon from '@mui/icons-material/Download';

import Sidebar from "../components/Sidebar"
import cookie from 'react-cookies'
import { formatDate, telechargerCSV, aujourdhui } from "../utils/csv"
import { optionsAnnees, anneeEnCours, dansAnnee, appliquerFiltres } from "../utils/filtres"


// ─────────────────────────────────────────────────────────────
// Statistiques de l'application, comme la page « Stats Application » de
// l'administration Play & Learn : première connexion et nombre
// d'ouvertures de l'app par étudiant, mêmes filtres, même export CSV.
// ─────────────────────────────────────────────────────────────
const AdminStatistiquesApp = () => {
  const history = useHistory()

  const user = cookie.load("user")
  const handleDisconnect = () => {
    cookie.save('user', "disconnected", {
      path: '/',
      maxAge: 60 * 60 * 1.5, //sec
    })
    history.push('/login');
  }

  if (user === null || user === undefined || user === "disconnected") history.push("/login");
  if (user !== null && user !== undefined && user.username !== undefined && !user.username.toUpperCase().includes("ADMIN")) handleDisconnect()

  // null : chargement ou erreur (voir etat)
  const [stats, setStats] = useState(null)
  const [etat, setEtat] = useState('chargement')   // 'chargement' | 'ok' | 'erreur'
  const [rechargement, setRechargement] = useState(0)

  const anneesOptions = useMemo(() => optionsAnnees(), [])
  // Année en cours par défaut, comme Play & Learn.
  const [annee, setAnnee] = useState(anneeEnCours())
  const [nom, setNom] = useState('Tous');
  const [prenom, setPrenom] = useState('Tous');
  const [pseudo, setPseudo] = useState('Tous');
  const [campus, setCampus] = useState('Tous');
  const [programme, setProgramme] = useState('Tous');
  const [groupe, setGroupe] = useState('Tous');

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
  };

  useEffect(() => {
    let actif = true
    const charger = async () => {
      setEtat('chargement')
      const donnees = await BackendAPI.getStatistiquesApp()
      if (!actif) return
      setStats(donnees)
      setEtat(donnees === null ? 'erreur' : 'ok')
    }
    charger()
    return () => { actif = false }
  }, [rechargement])

  // Année : date de la première connexion (même règle que Play & Learn).
  const lignesPeriode = useMemo(
    () => (stats || []).filter((l) => dansAnnee(l.premiereConnexionApplication, annee)),
    [stats, annee])

  const { options, retenues } = useMemo(
    () => appliquerFiltres(lignesPeriode, { campus, programme, groupe, nom, prenom, pseudo }),
    [lignesPeriode, campus, programme, groupe, nom, prenom, pseudo])

  // Un filtre dont la valeur n'est plus proposée revient à « Tous ».
  useEffect(() => { if (!options.campus.includes(campus)) setCampus("Tous") }, [options.campus, campus])
  useEffect(() => { if (!options.programme.includes(programme)) setProgramme("Tous") }, [options.programme, programme])
  useEffect(() => { if (!options.groupe.includes(groupe)) setGroupe("Tous") }, [options.groupe, groupe])
  useEffect(() => { if (!options.nom.includes(nom)) setNom("Tous") }, [options.nom, nom])
  useEffect(() => { if (!options.prenom.includes(prenom)) setPrenom("Tous") }, [options.prenom, prenom])
  useEffect(() => { if (!options.pseudo.includes(pseudo)) setPseudo("Tous") }, [options.pseudo, pseudo])

  // Du plus grand au plus petit nombre d'ouvertures.
  const lignes = useMemo(
    () => [...retenues].sort((a, b) =>
      (Number(b.nbOuverturesApplication) || 0) - (Number(a.nbOuverturesApplication) || 0) ||
      String(a.nom).localeCompare(String(b.nom)) || String(a.prenom).localeCompare(String(b.prenom))),
    [retenues])

  const totalOuvertures = lignes.reduce((t, l) => t + (Number(l.nbOuverturesApplication) || 0), 0)
  const libelleAnnee = annee === "Tous" ? "Toutes années" : annee

  const exportToCSV = () => {
    if (lignes.length === 0) {
      alert("Aucun utilisateur à exporter !");
      return;
    }
    // Mêmes colonnes que l'export « Stats Application » de Play & Learn.
    const headers = ["Nom", "Prénom", "Pseudo", "Email", "Campus", "Programme", "Groupe", "Première connexion", "Nombre ouvertures"];
    const rows = lignes.map((u) => [
      u.nom, u.prenom, u.pseudo, u.email, u.campus, u.programme, u.groupe,
      u.premiereConnexionApplication, Number(u.nbOuverturesApplication) || 0,
    ]);
    telechargerCSV("Statistiques application - " + libelleAnnee + " - " + aujourdhui() + ".csv", headers, rows)
  };

  const renderSelect = (label, value, setter, opts) => (
    <FormControl size="small" variant="outlined">
      <InputLabel>{label}</InputLabel>
      <Select value={value} onChange={handleFilterChange(setter)} label={label}>
        {opts.map((option) => (
          <MenuItem key={option} value={option} style={{ paddingTop: 8, paddingBottom: 8 }}>{option}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  const renderTableau = () => {
    if (etat === 'chargement') return <Typography color="textSecondary">Chargement des statistiques…</Typography>
    if (etat === 'erreur') return (
      <Typography color="error" component="div">
        Les statistiques n'ont pas pu être chargées (serveur injoignable).{" "}
        <Button size="small" color="primary" variant="outlined" onClick={() => setRechargement((n) => n + 1)}>Réessayer</Button>
      </Typography>
    )
    if (lignes.length === 0) return <Typography color="textSecondary">Aucune statistique pour ces filtres.</Typography>
    return (
      <TableContainer component={Paper} style={{ maxHeight: "100%" }} >
        <Table stickyHeader aria-label="statistiques application">
          <TableHead>
            <TableRow key={"HEAD"}>
              <TableCell align="center" style={{ width: 150 }}>Nom</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Prénom</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Pseudo</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Campus</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Programme</TableCell>
              <TableCell align="center" style={{ width: 150 }}>Groupe</TableCell>
              <TableCell align="center" style={{ width: 200 }}>Première connexion</TableCell>
              <TableCell align="center" style={{ width: 200 }}>Nombre ouvertures</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lignes.map((u) => (
              <TableRow key={u.idUser} style={{ height: 40 }}>
                <TableCell align="center">{u.nom}</TableCell>
                <TableCell align="center">{u.prenom}</TableCell>
                <TableCell align="center">{u.pseudo}</TableCell>
                <TableCell align="center">{u.campus}</TableCell>
                <TableCell align="center">{u.programme}</TableCell>
                <TableCell align="center">{u.groupe}</TableCell>
                <TableCell align="center">{formatDate(u.premiereConnexionApplication)}</TableCell>
                <TableCell align="center">{Number(u.nbOuverturesApplication) || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div
        className="body-admin"
      >
        <Typography variant="h1" color="primary">Statistiques Application</Typography>
        <Divider />
        <div style={{ margin: 10 }}  >
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            {renderSelect("Année", annee, setAnnee, anneesOptions)}
            {renderSelect("Nom", nom, setNom, options.nom)}
            {renderSelect("Prénom", prenom, setPrenom, options.prenom)}
            {renderSelect("Pseudo", pseudo, setPseudo, options.pseudo)}
            {renderSelect("Campus", campus, setCampus, options.campus)}
            {renderSelect("Programme", programme, setProgramme, options.programme)}
            {renderSelect("Groupe", groupe, setGroupe, options.groupe)}
            <Tooltip title="Exporter les statistiques affichées (CSV)" arrow>
              <IconButton aria-label="download" style={{ padding: 10 }} onClick={exportToCSV}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </div>
          <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: -10 }}>
            Année : étudiants dont la première connexion date de cette année scolaire (25 août → 24 août), comme Play & Learn.
            Nombre d'ouvertures : depuis la première connexion, toutes années confondues.
          </Typography>
          {etat === 'ok' && lignes.length > 0 ?
            <Typography variant="body2" style={{ marginTop: 10 }}>
              <b>{lignes.length}</b> étudiant{lignes.length > 1 ? "s" : ""} · <b>{totalOuvertures}</b> ouverture{totalOuvertures > 1 ? "s" : ""} de l'application
            </Typography>
            : null}
          <br></br>
          {renderTableau()}
        </div>
      </div></div>
  );
}

export default AdminStatistiquesApp;
